import { useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import type { AxiosError } from 'axios'

import { Alert } from '@gto/ui/alert'
import { Button } from '@gto/ui/button'
import { Dialog } from '@gto/ui/dialog'

import {
  type SiteTransferRedemptionOption,
  useRedemptionsRedemptionOptionsSiteTransferRedeemCreate,
} from '@/api/gen'
import { TextInput } from '@/components/text-input'
import { useEdgeBalance } from '@/hooks/use-edge-balance'
import { getAxiosErrorMessage } from '@/utils/helpers'

import { CryptoRedeemSum } from './crypto-redeem-sum'
import { RedeemOptionChip } from './redeem-option-chip'
import { RoomTransferDescription } from './room-transfer-description'

export type RoomRedeemModalProps = {
  open: boolean
  onClose: () => void
  edge: number
  room: SiteTransferRedemptionOption
}

export const RoomRedeemModal = ({ open, onClose, edge, room }: RoomRedeemModalProps) => {
  const [transaction, setTransaction] = useState<{
    status: 'success' | 'warning'
    message?: string
  } | null>(null)
  const { reduceBalance } = useEdgeBalance()
  const { formatMessage } = useIntl()

  const { mutate: confirmRedeem, isPending } =
    useRedemptionsRedemptionOptionsSiteTransferRedeemCreate({
      mutation: {
        onError: (err: AxiosError) => {
          setTransaction({
            status: 'warning',
            message:
              getAxiosErrorMessage(err) ||
              formatMessage({ id: 'edge.premiumRedeem.confirmRoomTransfer.error' }),
          })
        },
        onSuccess: (data) => {
          setTransaction({
            status: data.success ? 'success' : 'warning',
            message:
              data.message ||
              formatMessage({ id: 'edge.premiumRedeem.confirmRoomTransfer.success' }),
          })
          if (data.success) {
            reduceBalance(edge)
          }
        },
      },
    })

  const onSubmit = () => {
    setTransaction(null)

    confirmRedeem({
      data: {
        edge_amount: edge,
        poker_room_username: room.username,
        poker_site_id: room.poker_site_id,
      },
    })
  }

  const handleClose = () => {
    setTransaction(null)
    onClose()
  }

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          handleClose()
        }
      }}
    >
      <Dialog.Portal>
        <Dialog.Backdrop />
        <Dialog.Popup className="w-full max-w-[500px] sm:top-[60px] sm:translate-y-0">
          <Dialog.Header>
            <FormattedMessage
              id={
                transaction?.status === 'success'
                  ? 'edge.premiumRedeem.confirmRoomTransfer.success.title'
                  : 'edge.premiumRedeem.confirmRoomTransfer.title'
              }
            />
          </Dialog.Header>
          <Dialog.Content>
            <div className="flex flex-col gap-8">
              {transaction?.status === 'success' ? (
                <Alert
                  heading={transaction.message}
                  severity="success"
                />
              ) : (
                <>
                  <div>
                    <CryptoRedeemSum edge={edge} />
                    <RedeemOptionChip
                      className="flex-col gap-4 rounded-md border border-primary-main py-3.5"
                      icon={room.poker_room_logo}
                      imgWrapperClassName="w-[140px]"
                      name={room.poker_room_name}
                    />
                  </div>

                  <TextInput
                    label="common.labels.username"
                    input={{
                      size: 'medium',
                      value: room.username || '',
                      disabled: true,
                      className: 'pointer-events-none',
                    }}
                  />
                  {transaction?.status === 'warning' && (
                    <Alert
                      heading={transaction.message}
                      severity="warning"
                    />
                  )}
                  <RoomTransferDescription />
                </>
              )}
            </div>
          </Dialog.Content>
          <Dialog.Footer>
            <Button
              color="secondary"
              disabled={isPending}
              size="large"
              variant="contained"
              onClick={handleClose}
            >
              <FormattedMessage
                id={
                  transaction?.status === 'success'
                    ? 'common.controls.close'
                    : 'common.controls.cancel'
                }
              />
            </Button>
            {transaction?.status !== 'success' && (
              <Button
                color="primary"
                disabled={edge <= 0 || isPending}
                loading={isPending}
                size="large"
                variant="contained"
                onClick={onSubmit}
              >
                <FormattedMessage id="common.controls.completeRedemption" />
              </Button>
            )}
          </Dialog.Footer>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
